import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Mail, ClipboardList, ExternalLink, Download, CheckCircle, Zap, Loader2 } from 'lucide-react';
import { SmartApplyJob, useSmartApply } from '@/hooks/useSmartApply';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// Chrome Extension ID from environment
const CHROME_EXTENSION_ID = import.meta.env.VITE_CHROME_EXTENSION_ID;

interface SmartApplyModalProps {
  job: SmartApplyJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied: () => void;
}

// Helper function to convert blob to base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper function to convert file/URL to base64
const convertToBase64 = async (fileOrUrl: string | Blob | null): Promise<string | null> => {
  if (!fileOrUrl) return null;
  
  if (typeof fileOrUrl === 'string') {
    // Already base64
    if (fileOrUrl.startsWith('data:')) {
      return fileOrUrl;
    }
    // URL - fetch and convert
    if (fileOrUrl.startsWith('http')) {
      try {
        const response = await fetch(fileOrUrl);
        const blob = await response.blob();
        return blobToBase64(blob);
      } catch (error) {
        console.error('Failed to fetch and convert URL:', error);
        return null;
      }
    }
  }
  
  if (fileOrUrl instanceof Blob) {
    return blobToBase64(fileOrUrl);
  }
  
  return null;
};

export function SmartApplyModal({ job, open, onOpenChange, onApplied }: SmartApplyModalProps) {
  const [activeTab, setActiveTab] = useState('resume');
  const [isSendingToExtension, setIsSendingToExtension] = useState(false);
  const { updateStatus } = useSmartApply();

  // Generate resume HTML preview from JSON
  const generateResumePreview = () => {
    if (!job.custom_resume_json) return '';

    const resume = job.custom_resume_json as any;
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #2563EB; font-size: 28px; margin-bottom: 5px; }
          h2 { color: #1e40af; font-size: 18px; border-bottom: 2px solid #2563EB; padding-bottom: 5px; margin-top: 30px; }
          h3 { color: #000; font-size: 16px; margin-bottom: 5px; }
          .summary { margin: 20px 0; }
          .item { margin-bottom: 20px; }
          .item-header { display: flex; justify-content: space-between; }
          .date { color: #666; font-size: 14px; }
          ul { margin: 5px 0; padding-left: 20px; }
          .skills { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 10px; }
          .skill { background: #EFF6FF; color: #1e40af; padding: 5px 10px; border-radius: 4px; font-size: 14px; }
        </style>
      </head>
      <body>
        <h1>${resume.userName || 'Professional Resume'}</h1>
        ${resume.summary ? `<div class="summary">${resume.summary}</div>` : ''}
        ${resume.sections?.map((section: any) => `
          <h2>${section.title}</h2>
          ${section.items?.map((item: any) => `
            <div class="item">
              ${item.company || item.title ? `
                <div class="item-header">
                  <h3>${item.company || item.title}</h3>
                  ${item.start || item.end ? `<span class="date">${item.start} - ${item.end || 'Present'}</span>` : ''}
                </div>
              ` : ''}
              ${item.title && item.company ? `<div style="color: #666; margin-bottom: 5px;">${item.title}</div>` : ''}
              ${item.bullets?.length ? `<ul>${item.bullets.map((b: string) => `<li>${b}</li>`).join('')}</ul>` : ''}
            </div>
          `).join('')}
        `).join('')}
        ${resume.skills?.length ? `
          <h2>Skills</h2>
          <div class="skills">
            ${resume.skills.map((skill: string) => `<span class="skill">${skill}</span>`).join('')}
          </div>
        ` : ''}
        ${resume.education?.length ? `
          <h2>Education</h2>
          ${resume.education.map((edu: any) => `
            <div class="item">
              <div class="item-header">
                <h3>${edu.institution}</h3>
                <span class="date">${edu.years}</span>
              </div>
              <div style="color: #666;">${edu.degree}</div>
            </div>
          `).join('')}
        ` : ''}
      </body>
      </html>
    `;
    return html;
  };

  // Send job data directly to Chrome extension with base64 files
  const sendToExtension = async (): Promise<{ success: boolean; message?: string }> => {
    // Check if chrome.runtime is available
    const chromeApi = (window as Window & { chrome?: Chrome }).chrome;
    
    if (!chromeApi?.runtime?.sendMessage) {
      return { success: false, message: 'Chrome extension API not available. Please use Chrome browser.' };
    }

    if (!CHROME_EXTENSION_ID) {
      return { success: false, message: 'Extension ID not configured. Please contact support.' };
    }

    try {
      // Convert resume PDF to base64 if available
      const resumeBase64 = job.custom_resume_pdf_url 
        ? await convertToBase64(job.custom_resume_pdf_url)
        : null;

      // Extract user name from resume JSON for filename
      const resume = job.custom_resume_json as any;
      const userName = resume?.userName || 'Candidate';
      const nameParts = userName.split(' ');
      const firstName = nameParts[0] || 'User';
      const lastName = nameParts.slice(1).join('_') || 'Resume';

      // Build application data from form data and job info
      const formData = job.application_form_data as Record<string, any> || {};
      
      const applicationData = {
        action: 'autoApply',
        jobUrl: job.job_url,
        resumeData: resumeBase64 ? {
          base64: resumeBase64,
          filename: `${firstName}_${lastName}_Resume.pdf`,
          mimeType: 'application/pdf',
          json: job.custom_resume_json,
        } : {
          json: job.custom_resume_json,
        },
        coverLetter: job.custom_cover_letter_text ? {
          text: job.custom_cover_letter_text,
          filename: `${firstName}_${lastName}_Cover_Letter.txt`,
        } : null,
        applicationData: {
          jobId: job.id,
          jobTitle: job.job_title,
          companyName: job.company_name,
          location: job.location,
          atsScore: job.ats_score,
          matchScore: job.match_score,
          // Form data fields
          firstName: formData.first_name || formData.firstName || firstName,
          lastName: formData.last_name || formData.lastName || lastName,
          email: formData.email || '',
          phone: formData.phone || '',
          city: formData.city || formData.location || job.location || '',
          linkedin: formData.linkedin || formData.linkedin_url || '',
          website: formData.website || formData.portfolio || '',
          experience: formData.years_experience || formData.experience || '',
          sponsorship: formData.sponsorship || formData.work_authorization || '',
          // All form data for additional fields
          formData: formData,
        },
      };

      return new Promise((resolve) => {
        chromeApi.runtime.sendMessage(
          CHROME_EXTENSION_ID,
          applicationData,
          (response: unknown) => {
            if (chromeApi.runtime?.lastError) {
              console.error('Extension error:', chromeApi.runtime.lastError);
              resolve({ 
                success: false, 
                message: chromeApi.runtime.lastError.message || 'Chrome extension not installed or not responding' 
              });
            } else {
              console.log('Extension response:', response);
              const resp = response as { success?: boolean; message?: string; error?: string } | undefined;
              if (resp?.success) {
                resolve({ success: true, message: resp.message || 'Job sent to extension!' });
              } else {
                resolve({ success: true, message: resp?.message || 'Job sent to extension!' });
              }
            }
          }
        );
      });
    } catch (err) {
      console.error('Failed to prepare extension data:', err);
      return { success: false, message: 'Failed to prepare application data' };
    }
  };

  const handleOpenApplication = async () => {
    if (!job.job_url) {
      toast.error('No application URL available');
      return;
    }

    setIsSendingToExtension(true);

    try {
      // First, try to send directly to Chrome extension
      const extensionResult = await sendToExtension();
      
      if (extensionResult.success) {
        toast.success('Job sent to extension!', { 
          description: 'Your Chrome extension will auto-apply with your materials.',
          duration: 5000 
        });
        
        // Also create a task in the database for tracking
        await supabase.functions.invoke('smart-apply-to-extension', {
          body: { queueId: job.id, action: 'create_task' }
        }).catch(console.error); // Non-blocking
        
        onOpenChange(false);
        onApplied();
        return;
      }

      // If extension messaging failed, fall back to creating a task in the database
      console.log('Extension messaging failed, falling back to database task:', extensionResult.message);
      
      const { data, error } = await supabase.functions.invoke('smart-apply-to-extension', {
        body: { queueId: job.id, action: 'create_task' }
      });

      if (error) {
        console.error('Failed to create extension task:', error);
        // Final fallback: open job page manually
        window.open(job.job_url, '_blank');
        toast.info('Chrome extension not detected. Opening application page...', { 
          description: 'Install our Chrome extension for one-click auto-apply.',
          duration: 5000 
        });
      } else {
        toast.success('Task queued for extension!', { 
          description: 'Open the extension to auto-apply when ready.',
          duration: 5000 
        });
      }
    } catch (error) {
      console.error('Error:', error);
      window.open(job.job_url, '_blank');
      toast.info('Opening application page manually.', { duration: 3000 });
    } finally {
      setIsSendingToExtension(false);
    }
    
    onOpenChange(false);
    onApplied();
  };

  const handleDownloadResume = () => {
    const html = generateResumePreview();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
      toast.success('Opening print dialog... Save as PDF');
    }
  };

  const handleCopyCoverLetter = () => {
    if (job.custom_cover_letter_text) {
      navigator.clipboard.writeText(job.custom_cover_letter_text);
      toast.success('Cover letter copied to clipboard!');
    }
  };

  const handleCopyFormData = () => {
    if (job.application_form_data) {
      const text = Object.entries(job.application_form_data)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');
      navigator.clipboard.writeText(text);
      toast.success('Form data copied to clipboard!');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Review & Apply</DialogTitle>
          <DialogDescription>
            Review your AI-generated materials before applying
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Side: Materials */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="resume" className="gap-2">
                  <FileText className="h-4 w-4" />
                  Resume
                </TabsTrigger>
                <TabsTrigger value="cover-letter" className="gap-2">
                  <Mail className="h-4 w-4" />
                  Cover Letter
                </TabsTrigger>
                <TabsTrigger value="form-data" className="gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Application Data
                </TabsTrigger>
              </TabsList>

              <TabsContent value="resume" className="mt-4">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Generated Resume</h3>
                    {job.ats_score && (
                      <Badge className="bg-green-500/20 text-green-500">
                        ATS Score: {job.ats_score}/100
                      </Badge>
                    )}
                  </div>
                  
                  {job.custom_resume_json ? (
                    <div className="space-y-4">
                      <iframe
                        srcDoc={generateResumePreview()}
                        className="w-full h-[500px] border rounded"
                        title="Resume Preview"
                      />
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1 gap-2" onClick={handleDownloadResume}>
                          <Download className="h-4 w-4" />
                          Download as PDF
                        </Button>
                        <Button variant="outline" className="flex-1 gap-2" onClick={handleCopyCoverLetter}>
                          <ClipboardList className="h-4 w-4" />
                          Copy Cover Letter
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">Resume preview not available</p>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="cover-letter" className="mt-4">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Generated Cover Letter</h3>
                    <Button variant="outline" size="sm" onClick={handleCopyCoverLetter}>
                      Copy to Clipboard
                    </Button>
                  </div>
                  {job.custom_cover_letter_text ? (
                    <ScrollArea className="h-[500px]">
                      <Textarea
                        value={job.custom_cover_letter_text}
                        readOnly
                        className="min-h-[480px] font-serif text-base leading-relaxed"
                      />
                    </ScrollArea>
                  ) : (
                    <p className="text-muted-foreground">Cover letter not available</p>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="form-data" className="mt-4">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Application Form Data</h3>
                    <Button variant="outline" size="sm" onClick={handleCopyFormData}>
                      Copy All
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Copy and paste this data into the application form fields
                  </p>
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-4">
                      {job.application_form_data && Object.keys(job.application_form_data).length > 0 ? (
                        Object.entries(job.application_form_data).map(([key, value]) => (
                          <div key={key} className="border-b pb-2">
                            <p className="text-sm font-medium capitalize">
                              {key.replace(/_/g, ' ')}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {String(value)}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted-foreground">No form data available</p>
                      )}
                    </div>
                  </ScrollArea>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Side: Job Details */}
          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center gap-3 mb-3">
                {job.logo && (
                  <img 
                    src={job.logo} 
                    alt={job.company_name} 
                    className="w-12 h-12 rounded object-contain"
                  />
                )}
                <div>
                  <h4 className="font-semibold">{job.job_title}</h4>
                  <p className="text-sm text-muted-foreground">{job.company_name}</p>
                </div>
              </div>

              {job.location && (
                <p className="text-sm text-muted-foreground mb-2">{job.location}</p>
              )}

              {job.salary && (
                <p className="text-sm font-medium mb-2">{job.salary}</p>
              )}

              <div className="flex gap-2 flex-wrap mb-3">
                {job.job_type && (
                  <Badge variant="secondary">{job.job_type}</Badge>
                )}
                {job.remote && (
                  <Badge variant="outline">{job.remote}</Badge>
                )}
              </div>

              {job.match_score && (
                <div className="flex items-center justify-between py-2 border-t">
                  <span className="text-sm">Match Score:</span>
                  <span className="text-sm font-semibold text-green-500">
                    {job.match_score}%
                  </span>
                </div>
              )}
            </Card>

            {job.job_description && (
              <Card className="p-4">
                <h4 className="font-semibold mb-2">Job Description</h4>
                <ScrollArea className="h-[200px]">
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {job.job_description}
                  </p>
                </ScrollArea>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Primary Auto-Apply Button */}
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-primary/20 p-1.5 rounded">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <h4 className="font-semibold text-sm">Chrome Extension Auto-Apply</h4>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Click to create an automation task. Your Chrome extension will auto-fill the application form with your AI-generated materials.
                </p>
                <Button
                  className="w-full gap-2"
                  onClick={handleOpenApplication}
                  size="lg"
                  disabled={isSendingToExtension}
                >
                  {isSendingToExtension ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending to Extension...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      Auto-Apply via Extension
                    </>
                  )}
                </Button>
              </div>

              {/* Manual Options */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Manual Application:</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => job.job_url && window.open(job.job_url, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Job Page
                  </Button>
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={handleDownloadResume}
                  >
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  💡 <strong>Tip:</strong> Install our Chrome extension for one-click auto-apply. It automatically fills forms using your custom materials.
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
